import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Send } from "lucide-react";
import { Logo } from "./Logo";
import { SERVICES, TECHNOLOGIES, COMPANY } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Col = ({ title, children }) => (
  <div>
    <h4 className="text-xs font-mono font-semibold uppercase tracking-widest text-primary mb-4">{title}</h4>
    <ul className="space-y-2.5">{children}</ul>
  </div>
);

const FootLink = ({ to, children, testid }) => (
  <li><Link to={to} data-testid={testid} className="text-sm text-muted-foreground hover:text-primary transition-colors duration-200">{children}</Link></li>
);

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      if (process.env.REACT_APP_BACKEND_URL) {
        await axios.post(`${API}/newsletter`, { email });
      } else {
        await fetch("/", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams({ "form-name": "newsletter", email }).toString(),
        });
      }
      toast.success("Subscribed! Welcome to the VAELIX newsletter.");
      setEmail("");
    } catch {
      toast.error("Please enter a valid email address.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="dark border-t border-border/60 bg-background text-foreground">
      <div className="container-x py-14 grid gap-10 md:grid-cols-2 lg:grid-cols-5">
        <div className="lg:col-span-2 max-w-sm">
          <Logo testid="footer-logo" />
          <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
            Transforming businesses through technology. Enterprise software, AI, cloud and digital experiences engineered in Pune for the world.
          </p>
          <a href={`mailto:${COMPANY.email}`} data-testid="footer-email-link"
            className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors">
            <Mail size={15} className="text-primary" /> {COMPANY.email}
          </a>
          <form onSubmit={subscribe} className="mt-5" data-testid="newsletter-form">
            <label htmlFor="newsletter-email" className="text-xs font-mono uppercase tracking-widest text-primary">Newsletter</label>
            <div className="mt-2 flex rounded-xl border border-border bg-card overflow-hidden focus-within:border-primary/50 transition-colors">
              <input id="newsletter-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="newsletter-email-input" placeholder="you@company.com"
                className="flex-1 min-w-0 bg-transparent px-4 py-2.5 text-sm outline-none placeholder:text-muted-foreground" />
              <button type="submit" disabled={busy} data-testid="newsletter-subscribe-button" aria-label="Subscribe"
                className="px-4 bg-gradient-to-r from-indigo-500 to-blue-500 text-white hover:opacity-90 transition-opacity disabled:opacity-50">
                <Send size={16} />
              </button>
            </div>
          </form>
        </div>
        <Col title="Quick Links">
          <FootLink to="/" testid="footer-link-home">Home</FootLink>
          <FootLink to="/about" testid="footer-link-about">About Us</FootLink>
          <FootLink to="/why-us" testid="footer-link-why-us">Why Us</FootLink>
          <FootLink to="/process" testid="footer-link-process">Process</FootLink>
          <FootLink to="/contact" testid="footer-link-contact">Contact</FootLink>
        </Col>
        <Col title="Services">
          {SERVICES.slice(0, 5).map((s) => (
            <FootLink key={s.id} to="/services" testid={`footer-service-${s.id}`}>{s.title}</FootLink>
          ))}
        </Col>
        <Col title="Technologies">
          {TECHNOLOGIES.flatMap((c) => c.items).slice(0, 6).map((t) => (
            <FootLink key={t} to="/technologies" testid={`footer-tech-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{t}</FootLink>
          ))}
        </Col>
      </div>
      <div className="border-t border-border/60">
        <div className="container-x py-5 pr-20 sm:pr-24 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
          <p data-testid="footer-copyright">© {new Date().getFullYear()} {COMPANY.name}. All rights reserved. | {COMPANY.location}</p>
          <div className="flex items-center gap-5">
            <Link to="#" data-testid="footer-privacy-link" className="hover:text-primary transition-colors">Privacy Policy</Link>
            <Link to="#" data-testid="footer-terms-link" className="hover:text-primary transition-colors">Terms</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
